import { useMemo, useState } from "react";
import { seo } from "@/lib/seo";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "motion/react";
import {
  ArrowLeft,
  BookOpen,
  Check,
  ClipboardList,
  Lock,
  Pencil,
  Trash2,
  TriangleAlert,
  Trophy,
} from "lucide-react";
import { fetchTests, removeTest, saveTestScoreFn, toggleTestItem } from "@/lib/tests.functions";
import { formatPrettyDate, localDateString, subjectAccent, subjectTint } from "@/lib/app-types";
import {
  STATUS_LABEL,
  STATUS_STYLE,
  canEditScore,
  daysBetween,
  groupTestDays,
  itemState,
  percentOf,
  planProgress,
  testStatus,
  type TestItem,
  type TestPlan,
} from "@/lib/test-plan";
import { buildInsights } from "@/lib/test-performance";
import { TestScoreSheet, type ScoreDraft } from "@/components/app/test-score-sheet";
import { NeuralButton } from "@/components/app/neural-button";
import { notifySaved, trackSave } from "@/lib/save-feedback";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/tests/$testId")({
  head: () => ({
    ...seo({
      title: "Test Details — The Quantum Minds",
      description:
        "Your day-by-day schedule, revision and practice tasks, progress timeline and score for this test.",
      noindex: true,
    }),
  }),
  component: TestDetailPage,
});

function TestDetailPage() {
  const { testId } = Route.useParams();
  const today = localDateString();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [scoreSheet, setScoreSheet] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isPending } = useQuery({ queryKey: ["tests"], queryFn: () => fetchTests() });
  const test = useMemo<TestPlan | undefined>(
    () => (data ?? []).find((t) => t.id === testId),
    [data, testId],
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["tests"] });

  const toggle = useMutation({
    mutationFn: (vars: { itemId: string; completed: boolean }) =>
      toggleTestItem({ data: { ...vars, today } }),
    retry: 2,
    onMutate: (vars) => {
      const stop = trackSave("toggle-test-item");
      setError(null);
      const previous = queryClient.getQueryData<TestPlan[]>(["tests"]);
      queryClient.setQueryData<TestPlan[]>(["tests"], (old) =>
        (old ?? []).map((t) => ({
          ...t,
          items: t.items.map((item) =>
            item.id === vars.itemId
              ? {
                  ...item,
                  status: vars.completed ? ("completed" as const) : ("pending" as const),
                  completedAt: vars.completed ? new Date().toISOString() : null,
                }
              : item,
          ),
        })),
      );
      if (vars.completed) notifySaved("Task completed");
      return { previous, stop };
    },
    onError: (err: Error, _vars, ctx) => {
      ctx?.stop("error");
      if (ctx?.previous) queryClient.setQueryData(["tests"], ctx.previous);
      setError(err.message);
    },
    onSuccess: (_r, _v, ctx) => ctx?.stop("ok"),
    onSettled: invalidate,
  });

  const saveScore = useMutation({
    mutationFn: (draft: ScoreDraft) => saveTestScoreFn({ data: { testId, today, ...draft } }),
    onSuccess: () => {
      setScoreSheet(false);
      setError(null);
      notifySaved("Score saved");
      invalidate();
    },
    onError: (err: Error) => setError(err.message),
  });

  const destroy = useMutation({
    mutationFn: () => removeTest({ data: { testId } }),
    onSuccess: () => {
      notifySaved("Test deleted");
      invalidate();
      navigate({ to: "/tests" });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (isPending) {
    return (
      <main className="page-shell px-5 lg:px-8 pt-8">
        <div className="glass h-48 animate-pulse rounded-[26px]" />
      </main>
    );
  }

  if (!test) {
    return (
      <main className="page-shell px-5 lg:px-8 pt-8 text-center">
        <p className="text-[15px] font-semibold">This test no longer exists.</p>
        <Link to="/tests" className="mt-4 inline-block text-[13px] font-semibold text-primary">
          Back to Test Tracker
        </Link>
      </main>
    );
  }

  const { days, carried } = groupTestDays(test, today);
  const progress = planProgress(test);
  const status = testStatus(test, today);
  const daysLeft = Math.max(0, daysBetween(today, test.testDate));
  const scoreOpen = canEditScore(test, today);
  const insights = test.score ? buildInsights([test], today) : [];

  return (
    <main className="page-shell px-5 lg:px-8 pt-8">
      <div className="flex items-center gap-3">
        <Link
          to="/tests"
          aria-label="Back to Test Tracker"
          className="flex size-9 shrink-0 items-center justify-center rounded-full border border-hairline bg-surface"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[22px] font-bold tracking-tight">{test.name}</h1>
          <p className="text-[12.5px] text-muted-foreground">{formatPrettyDate(test.testDate)}</p>
        </div>
        <span
          className={cn(
            "shrink-0 rounded-full border px-3 py-1 text-[11px] font-bold",
            STATUS_STYLE[status],
          )}
        >
          {STATUS_LABEL[status]}
        </span>
      </div>

      {error ? (
        <p className="mt-4 rounded-[20px] border border-destructive/25 bg-destructive/10 px-4 py-3 text-[12.5px] text-destructive">
          {error}
        </p>
      ) : null}

      <section className="glass mt-5 rounded-[26px] p-5">
        <div className="flex items-center justify-between text-[11.5px] font-semibold text-muted-foreground">
          <span>
            {progress.done}/{progress.total} tasks done
          </span>
          <span>
            {status === "completed" || status === "missed"
              ? `${progress.percent}%`
              : `${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`}
          </span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-surface">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress.percent}%` }}
            transition={{ type: "spring", stiffness: 120, damping: 24 }}
            className="neural-gradient h-full rounded-full"
          />
        </div>

      </section>

      <ScoreSection
        test={test}
        open={scoreOpen}
        onEnter={() => setScoreSheet(true)}
      />

      {insights.length ? (
        <section className="glass mt-5 rounded-[26px] p-5">
          <h2 className="text-[15px] font-bold tracking-tight">AI insights</h2>
          <div className="mt-3 space-y-2.5">
            {insights.map((i) => (
              <div key={i.label} className="rounded-[18px] border border-hairline bg-surface p-3.5">
                <p className="text-[10.5px] font-bold uppercase tracking-wider text-primary">
                  {i.label}
                </p>
                <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{i.body}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {carried.length ? (
        <div className="mt-5 flex items-center gap-2 rounded-2xl border border-warning/25 bg-warning/10 px-4 py-2.5 text-[12px] font-semibold text-warning">
          <TriangleAlert className="size-3.5 shrink-0" />
          {carried.length} overdue {carried.length === 1 ? "task" : "tasks"} — still completable
        </div>
      ) : null}

      <section className="mt-6 space-y-4">
        <h2 className="text-[15px] font-bold tracking-tight">Daily schedule</h2>
        {days.map((day) => (
          <div key={day.date}>
            <div className="flex items-center gap-2">
              <p
                className={cn(
                  "text-[11px] font-bold uppercase tracking-wider",
                  day.date === today ? "text-primary" : "text-muted-foreground",
                )}
              >
                {day.date === today ? "Today" : formatPrettyDate(day.date)}
              </p>
              <span className="h-px flex-1 bg-surface" />
            </div>
            <div className="mt-2.5 space-y-2">
              {day.items.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  today={today}
                  onToggle={() => toggle.mutate({ itemId: item.id, completed: item.status !== "completed" })}
                />
              ))}
            </div>
          </div>
        ))}
      </section>

      <button
        type="button"
        onClick={() => destroy.mutate()}
        className="mt-8 flex w-full items-center justify-center gap-2 rounded-full border border-destructive/25 bg-destructive/10 px-4 py-3 text-[12.5px] font-semibold text-destructive"
      >
        <Trash2 className="size-3.5" /> Delete this test
      </button>


      <TestScoreSheet
        open={scoreSheet}
        busy={saveScore.isPending}
        score={test.score}
        onClose={() => setScoreSheet(false)}
        onSubmit={(draft) => saveScore.mutate(draft)}
      />

    </main>
  );
}

function ScoreSection({
  test,
  open,
  onEnter,
}: {
  test: TestPlan;
  open: boolean;
  onEnter: () => void;
}) {
  const score = test.score;

  if (score) {
    const pct = percentOf(score.obtained, score.total);
    const subjects = (
      [
        ["Physics", score.physics],
        ["Chemistry", score.chemistry],
        ["Mathematics", score.maths],
      ] as const
    ).filter(([, v]) => v !== null) as [string, number][];
    const ranked = [...subjects].sort((a, b) => b[1] - a[1]);

    return (
      <section className="glass mt-5 rounded-[26px] p-5">
        <div className="flex items-center gap-2">
          <Trophy className="size-4 text-primary" />
          <h2 className="text-[15px] font-bold tracking-tight">Test score</h2>
        </div>
        <p className="mt-3 text-[30px] font-extrabold leading-none">
          <span className="neural-text">
            {score.obtained}
            <span className="text-[18px] text-muted-foreground">/{score.total}</span>
          </span>
        </p>
        <p className="mt-1.5 text-[13px] font-semibold text-muted-foreground">{pct}%</p>

        {subjects.length ? (
          <div className="mt-4 space-y-2">
            {subjects.map(([label, value]) => (
              <div
                key={label}
                className="flex items-center justify-between rounded-[18px] border border-hairline bg-surface px-4 py-2.5"
              >
                <span className="text-[12.5px] font-semibold">{label}</span>
                <span className="text-[12.5px] font-bold text-muted-foreground">
                  {value} · {percentOf(value, score.total / 3)}%
                </span>
              </div>
            ))}
            {ranked.length > 1 ? (
              <p className="pt-1 text-[12px] text-muted-foreground">
                Strongest: <span className="font-semibold text-success">{ranked[0][0]}</span> ·
                Weakest:{" "}
                <span className="font-semibold text-warning">{ranked[ranked.length - 1][0]}</span>
              </p>
            ) : null}
          </div>
        ) : null}

        {open ? (
          <button
            type="button"
            onClick={onEnter}
            className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-full border border-hairline bg-surface px-4 py-2.5 text-[12.5px] font-semibold"
          >
            <Pencil className="size-3.5" /> Edit score
          </button>
        ) : (
          <p className="mt-4 flex items-center justify-center gap-1.5 rounded-full border border-hairline bg-surface px-4 py-2.5 text-[12px] font-semibold text-muted-foreground">
            <Lock className="size-3.5" /> Score editing is locked
          </p>
        )}
      </section>
    );
  }

  return (
    <section className="glass mt-5 rounded-[26px] p-5">
      <div className="flex items-center gap-2">
        <Trophy className="size-4 text-primary" />
        <h2 className="text-[15px] font-bold tracking-tight">Test score</h2>
      </div>
      {open ? (
        <NeuralButton className="mt-4 w-full" onClick={onEnter}>
          <Trophy className="size-4" /> Enter Test Score
        </NeuralButton>
      ) : (
        <p className="mt-3 flex items-center gap-2 rounded-[18px] border border-hairline bg-surface px-4 py-3 text-[12.5px] text-muted-foreground">
          <Lock className="size-3.5 shrink-0" /> You can enter your score on the test day.
        </p>
      )}
    </section>
  );
}

function ItemRow({
  item,
  today,
  onToggle,
}: {
  item: TestItem;
  today: string;
  onToggle: () => void;
}) {
  const state = itemState(item, today);
  const done = state === "completed";
  const locked = state === "locked";
  const Icon = item.kind === "practice" ? ClipboardList : BookOpen;

  return (
    <motion.button
      type="button"
      disabled={locked}
      aria-disabled={locked}
      whileTap={locked ? undefined : { scale: 0.98 }}
      transition={{ type: "spring", stiffness: 520, damping: 20 }}
      onClick={locked ? undefined : onToggle}
      className={cn(
        "flex w-full items-center gap-3 rounded-[20px] border p-3.5 text-left transition-colors",
        done
          ? "border-success/25 bg-success/8"
          : state === "overdue"
            ? "border-warning/25 bg-warning/8"
            : locked
              ? "cursor-not-allowed border-hairline/60 bg-transparent opacity-55"
              : "border-hairline bg-surface",
      )}
    >
      <span
        className={cn(
          "flex size-6 shrink-0 items-center justify-center rounded-full border transition-colors",
          done ? "border-success bg-success" : "border-hairline",
          locked && "border-dashed",
        )}
      >
        {done ? (
          <Check className="size-3.5 text-background" />
        ) : locked ? (
          <Lock className="size-3 text-muted-foreground" />
        ) : null}
      </span>
      <span
        className={cn("size-2 shrink-0 rounded-full bg-gradient-to-br", subjectAccent(item.subject))}
      />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block text-[10px] font-semibold uppercase tracking-wider",
            subjectTint(item.subject),
          )}
        >
          {item.subject}
          {item.weak ? <span className="ml-1.5 text-warning">· Weak</span> : null}
        </span>
        <span
          className={cn(
            "block truncate text-[14px] font-semibold",
            done && "text-muted-foreground line-through",
          )}
        >
          {item.chapter}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-surface px-2.5 py-1 text-[10.5px] font-semibold text-muted-foreground">
        <Icon className="size-3" />
        {item.kind === "practice" ? "Practice" : `Revision ${item.passNumber}`}
      </span>
    </motion.button>
  );
}
